import { gql } from '@apollo/client';
import { AsyncButton, Button } from '@components/button';
import { Form, FormSelect, FormTextInput } from '@components/form';
import {
  CreateRemoteSessionGetOrgChannelsQueryHookResult,
  useCreateRemoteDevelopmentMutation,
  useCreateRemoteSessionGetOrgChannelsLazyQuery,
  useCreateRemoteSessionGetUserOrgsQuery,
} from '@generated/graphql';
import { MenuItem, Stack, Typography } from '@mui/material';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import * as yup from 'yup';

gql`
  query CreateRemoteSessionGetUserOrgs {
    getOrganizations {
      expired
      name
      organizationId
    }
  }
`;

gql`
  query CreateRemoteSessionGetOrgChannels($organizationId: String) {
    getChannels(organizationId: $organizationId) {
      channelId
      instanceType
      name
      organizationId
      state
      version
    }
  }
`;

const validationSchema = yup.object({
  organizationId: yup.string().required('Organization id is required'),
  channelId: yup.string().required('Channel id is required'),
  instanceType: yup.string(),
});

export const CreateRemoteSessionForChannelModal = ({
  channelId,
  organizationId,
  defaultInstanceType,
  onClose,
}: {
  channelId: string;
  organizationId: string;
  defaultInstanceType: string;
  onClose: VoidFunction;
}) => {
  const [createSession] = useCreateRemoteDevelopmentMutation();
  const handleFormSubmit = async (values: {
    channelId: string;
    organizationId: string;
    instanceType: string;
  }) => {
    posthog.capture('user_create_remote-session', {
      channelId: values.channelId,
      instanceType: values.instanceType,
    });
    const newWindow = window.open('', '_blank');
    const { data } = await createSession({
      variables: { channelId: values.channelId, instanceType: values.instanceType },
    });
    if (data?.createRemoteDevelopment?.editorUrl && typeof window !== 'undefined') {
      newWindow.location = data.createRemoteDevelopment.editorUrl;
      newWindow.focus();
    }
    onClose();
  };
  return (
    <Form
      initialValues={{
        organizationId,
        channelId,
        instanceType: defaultInstanceType,
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      validateOnBlur={false}
      validateOnChange
      validateOnMount
    >
      {({ isValid, handleSubmit, isSubmitting }) => {
        return (
          <Stack>
            <FormTextInput name="instanceType" label="Instance type" />
            <Stack gap={4}>
              <AsyncButton
                fullWidth
                loading={isSubmitting}
                disabled={!(isValid && !isSubmitting)}
                onClick={handleSubmit}
                data-cy="New-Workspace-Modal-Create-Button"
              >
                Create
              </AsyncButton>
              <Button
                fullWidth
                variant="secondary"
                data-cy="New-Workspace-Modal-Cancel-Button"
                onClick={onClose}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        );
      }}
    </Form>
  );
};

export const CreateRemoteSessionModal = ({ onClose }: { onClose: VoidFunction }) => {
  const { data } = useCreateRemoteSessionGetUserOrgsQuery();
  const [getOrgChannels] = useCreateRemoteSessionGetOrgChannelsLazyQuery();
  const [createSession] = useCreateRemoteDevelopmentMutation();
  const [orgChannels, setOrgChannels] = useState<
    CreateRemoteSessionGetOrgChannelsQueryHookResult['data']['getChannels'] | null
  >(null);
  const handleFormSubmit = async (values: {
    channelId: string;
    organizationId: string;
    instanceType: string;
  }) => {
    posthog.capture('user_create_remote-session', {
      channelId: values.channelId,
      instanceType: values.instanceType,
    });
    await createSession({
      variables: { channelId: values.channelId, instanceType: values.instanceType },
    });
    onClose();
  };

  return (
    <Form
      initialValues={{
        organizationId: '',
        channelId: '',
        instanceType: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      validateOnBlur={false}
      validateOnChange
    >
      {({ isValid, handleSubmit, dirty, isSubmitting, values, setFieldValue }) => {
        useEffect(() => {
          if (values.organizationId) {
            void getOrgChannels({ variables: { organizationId: values.organizationId } }).then(
              (res) => {
                setOrgChannels(
                  res.data?.getChannels.filter(
                    (channel) =>
                      channel.organizationId === values.organizationId &&
                      (channel.state === 'ready' ||
                        (channel.state === 'deploying' && parseInt(channel.version, 10) >= 1)),
                  ) || [],
                );
              },
            );
          }
        }, [values.organizationId]);
        useEffect(() => {
          if (values.channelId) {
            setFieldValue(
              'instanceType',
              orgChannels?.find((channel) => channel.channelId === values.channelId)
                ?.instanceType || '',
            );
          }
        }, [values.channelId]);
        return (
          <Stack>
            <FormSelect name="organizationId" label="Organization *">
              {data?.getOrganizations
                .filter((org) => !org.expired)
                .map((org) => (
                  <MenuItem key={org.organizationId} value={org.organizationId}>
                    {org.name}
                  </MenuItem>
                ))}
            </FormSelect>
            {values.organizationId && orgChannels?.length > 0 ? (
              <>
                <FormSelect name="channelId" label="Channel *">
                  {orgChannels.map((channel) => (
                    <MenuItem key={channel.channelId} value={channel.channelId}>
                      {channel.name}
                    </MenuItem>
                  ))}
                </FormSelect>
                <FormTextInput name="instanceType" label="Instance type" />
              </>
            ) : values.organizationId && orgChannels?.length === 0 ? (
              <Typography variant="body2" sx={{ mb: 4, maxWidth: '60ch' }}>
                This organization has no channels available for remote development. Please deploy a
                channel or select a different organization.
              </Typography>
            ) : null}
            <Stack gap={4}>
              <AsyncButton
                fullWidth
                loading={isSubmitting}
                disabled={!(isValid && dirty && !isSubmitting)}
                onClick={handleSubmit}
                data-cy="New-Workspace-Modal-Create-Button"
              >
                Create
              </AsyncButton>
              <Button
                fullWidth
                variant="secondary"
                data-cy="New-Workspace-Modal-Cancel-Button"
                onClick={onClose}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        );
      }}
    </Form>
  );
};
